import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Icon from "../AppIcon";

const AuthModal = () => {
  const { authModal, closeAuthModal, signUp, signIn, signInAsGuest } =
    useAuth();
  const [isSigningUp, setIsSigningUp] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const validateEmail = (value) => {
    if (!value.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)) {
      setEmailError("Invalid email format");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePassword = (value) => {
    if (value.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setLoading(true);
    try {
      if (isSigningUp) {
        await signUp(email, password);
      } else {
        await signIn(email, password);
      }
      closeAuthModal();
    } catch (err) {
      console.log("Auth error:", err?.error?.errors?.[0]?.message);
      setError(
        err?.code?.split("/")[1]?.split("-").join(" ") || "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGuest = async () => {
    setError("");
    setLoading(true);
    try {
      await signInAsGuest();
      closeAuthModal();
    } catch (err) {
      setError(
        err.code.split("/")[1].split("-").join(" ") || "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!authModal.isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[999] flex items-center justify-center p-4"
      onClick={closeAuthModal}
    >
      <div
        className="bg-card rounded-lg shadow-2xl w-full max-w-sm p-6 animate-scale-in relative "
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground cursor-pointer"
          onClick={closeAuthModal}
        >
          <Icon name="X" size={16} />
        </button>
        <h2 className="text-xl font-bold text-foreground mb-2 text-center">
          {isSigningUp ? "Create Account" : "Sign In"}
        </h2>
        <p className="text-muted-foreground text-center mb-6">
          To save your cart and view My Orders.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              validateEmail(e.target.value);
            }}
            error={emailError}
            required
          />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              validatePassword(e.target.value);
            }}
            error={passwordError}
            required
          />
          {error && <p className="text-sm text-destructive ">{error}</p>}
          <Button type="submit" loading={loading} fullWidth>
            {isSigningUp ? "Create Account" : "Sign In"}
          </Button>
        </form>

        <div className="text-center my-4">
          <button
            onClick={() => setIsSigningUp(!isSigningUp)}
            className="text-sm text-primary hover:underline"
          >
            {isSigningUp
              ? "Already have an account? Sign In"
              : "Need an account? Sign Up"}
          </button>
        </div>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">Or</span>
          </div>
        </div>

        <Button
          variant="outline"
          onClick={handleGuest}
          loading={loading}
          fullWidth
        >
          <Icon name="User" className="mr-2" />
          Continue as Guest
        </Button>
      </div>
    </div>
  );
};

export default AuthModal;
